import React, { useState } from 'react';
import { Menu, BookOpen, RefreshCw, FileText, Edit, HelpCircle, X, Maximize } from 'lucide-react';
import { useBranding, BrandLogo } from '../../branding';
import { getVersionInfo } from '../../utils/version';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { LanguageSelector } from './LanguageSelector';
import { isFullscreenPreferred, isFullscreenSupported, setFullscreenPreferred } from '../../utils/fullscreen';
import { DEFAULT_CONFIG, MenuVisibility } from '../../hooks/useAppConfig';

interface BurgerMenuProps {
  onNewMatch: () => void;
  onOpenChangelog: () => void;
  onOpenRules: () => void;
  onOpenQuiz: () => void;
  onEditMatch?: () => void;
  showEditMatch?: boolean;
  /** Entries to show (config.json `ui.<mode>`); everything when absent. */
  visibility?: MenuVisibility;
}

/**
 * Burger Menu - Menu hamburger fixe en haut à gauche
 * Contient : logos, version/changelog, règles, nouveau match. Une tablette pairée (mode kiosque)
 * masque "Modifier le match" et "Nouveau match" : c'est l'organisateur qui réinitialise la
 * tablette depuis le manager du centre.
 */
export function BurgerMenu({
  onNewMatch, onOpenChangelog, onOpenRules, onOpenQuiz, onEditMatch, showEditMatch,
  visibility = DEFAULT_CONFIG.ui.standalone,
}: BurgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { logo1, logo2 } = useBranding();
  const versionInfo = getVersionInfo();
  const { t } = useTranslation();
  const [fullscreenPreferred, setFullscreenPreferredState] = useState(isFullscreenPreferred);

  // A preference only: fullscreen itself starts with the warmup or the match (BeforeMatch)
  const toggleFullscreenPreference = () => {
    const next = !fullscreenPreferred;
    setFullscreenPreferred(next);
    setFullscreenPreferredState(next);
  };

  const handleOpenChangelog = () => {
    setIsOpen(false);
    onOpenChangelog();
  };

  const handleOpenRules = () => {
    setIsOpen(false);
    onOpenRules();
  };

  const handleOpenQuiz = () => {
    setIsOpen(false);
    onOpenQuiz();
  };

  const handleNewMatch = () => {
    setIsOpen(false);
    onNewMatch();
  };

  const handleEditMatch = () => {
    setIsOpen(false);
    if (onEditMatch) {
      onEditMatch();
    }
  };

  return (
    <>
      {/* Floating burger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-2 left-2 z-40 bg-black/40 text-white p-2 rounded-lg hover:bg-black/60 transition-colors"
        aria-label="Open menu"
      >
        <Menu size={24} />
      </button>

      {/* Menu panel - slide from left */}
      {isOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute left-0 top-0 h-full w-[80vw] landscape:w-[40vw] max-w-[300px] bg-white flex flex-col">
            {/* Menu items */}
            <div className="flex-1 p-4 space-y-2">
              {/* Language selector */}
              {visibility.language && <LanguageSelector />}

              {/* Fullscreen preference (no entry where pages cannot go fullscreen, e.g. iPhone) */}
              {visibility.fullscreen && isFullscreenSupported() && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={fullscreenPreferred}
                  onClick={toggleFullscreenPreference}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Maximize size={20} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium">{t('menu.fullscreen.label')}</div>
                    <div className="text-xs text-gray-500">{t('menu.fullscreen.hint')}</div>
                  </div>
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${fullscreenPreferred ? 'bg-green-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${fullscreenPreferred ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </span>
                </button>
              )}

              <hr className="my-2 border-gray-200" />

              {/* Close */}
              <button
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X size={20} className="text-gray-500" />
                <span className="font-medium">{t('common.close')}</span>
              </button>

              {(showEditMatch || visibility.newMatch) && <hr className="my-4 border-gray-200" />}

              {/* Edit match - only shown for manual matches */}
              {showEditMatch && (
                <button
                  onClick={handleEditMatch}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Edit size={20} className="text-gray-500" />
                  <span className="font-medium">{t('menu.editMatch')}</span>
                </button>
              )}

              {/* Nouveau match */}
              {visibility.newMatch && (
                <button
                  onClick={handleNewMatch}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                >
                  <RefreshCw size={20} />
                  <span className="font-medium">{t('menu.newMatch')}</span>
                </button>
              )}

              <hr className="my-4 border-gray-200" />

              {/* Referee reference */}
              {visibility.rules && (
                <button
                  onClick={handleOpenRules}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FileText size={20} className="text-gray-500" />
                  <span className="font-medium">{t('menu.rules')}</span>
                </button>
              )}

              {/* Quiz arbitre */}
              {visibility.quiz && (
                <button
                  onClick={handleOpenQuiz}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <HelpCircle size={20} className="text-gray-500" />
                  <span className="font-medium">{t('quiz.title')}</span>
                </button>
              )}

              {(visibility.rules || visibility.quiz) && <hr className="my-4 border-gray-200" />}

              {/* Version / Changelog */}
              {visibility.version && (
                <button
                  onClick={handleOpenChangelog}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <BookOpen size={20} className="text-gray-500" />
                  <div>
                    <div className="font-medium">{t('menu.version')}</div>
                    <div className="text-sm text-gray-500">{versionInfo.display}</div>
                  </div>
                </button>
              )}

              {visibility.version && <hr className="my-4 border-gray-200" />}

              {/* Branding logos */}
              {(logo1 || logo2) && (
                <div className="flex items-center justify-center gap-4 py-2">
                  {logo1 && <BrandLogo src={logo1} alt="Logo" className="h-10 w-auto" />}
                  {logo2 && <BrandLogo src={logo2} alt="Logo 2" className="h-10 w-auto" />}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 text-center text-xs text-gray-400">
              Build: {versionInfo.buildNumber}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
